import { captureException } from '@sentry/nextjs';

import { DEFAULT_CURRENCY } from 'constants/tickets.constants';
import {
  AccountTicketFragment,
  BookObjectInfoFragment,
  EventFragment,
  MoneyInfoFragment,
} from 'types/api/tickets.types';
import { EnhancedTicketInfo, LocalCartItem } from 'types/tickets.types';

export const getTicketPrice = (
  price:
    | Pick<EnhancedTicketInfo, 'originalPrice' | 'discountedPrice'>
    | undefined,
) => {
  const { originalPrice, discountedPrice } = price ?? {};
  const calculatedDiscountedPrice = !!discountedPrice
    ? discountedPrice
    : originalPrice;
  const calculatedOriginalPrice =
    !!discountedPrice &&
    !!originalPrice &&
    originalPrice.amount > discountedPrice.amount
      ? originalPrice
      : undefined;

  return {
    originalPrice: calculatedOriginalPrice,
    discountedPrice: calculatedDiscountedPrice,
  };
};

export const getTicketAndExtraTicketIds = (event: EventFragment | undefined) =>
  Array.from(
    event?.tickets?.reduce<Set<string>>((memo, ticket) => {
      memo.add(ticket.id);
      ticket.extraItems?.forEach((extraItem) => memo.add(extraItem.id));
      return memo;
    }, new Set()) ?? [],
  );

export const hasSeatedItems = (items: Array<LocalCartItem>) =>
  items.some((item) => isSeatedItem(item?.bookObjectsInfo));

export const isSeatedItem = (
  bookedObjectInfo: Array<BookObjectInfoFragment> | undefined,
) => !!bookedObjectInfo?.length;

export const getItemCount = (items?: Array<{ quantity: number }>) =>
  items?.reduce((memo, item) => memo + (item.quantity ?? 0), 0) ?? 0;

export const getAllItemsCount = (items?: Array<LocalCartItem>) =>
  getItemCount(items);

interface TicketBarcodePdf extends AccountTicketFragment {
  scannable: {
    __typename: 'TicketBarcodePdf';
    pdfTicketId: string;
  };
}

export const isPdfTicket = (
  ticket: AccountTicketFragment,
): ticket is TicketBarcodePdf =>
  ticket.scannable.__typename === 'TicketBarcodePdf' &&
  !!ticket.scannable.pdfTicketId;

export const toMoneyInfo = (
  amount: MoneyInfoFragment | number | undefined,
  currency: string = DEFAULT_CURRENCY,
) =>
  typeof amount === 'number'
    ? {
        amount,
        currency,
      }
    : amount;

export const toTotalPrice = (
  price: MoneyInfoFragment | undefined,
  quantity: number,
) => ({
  amount: (price?.amount ?? 0) * quantity,
  currency: price?.currency ?? DEFAULT_CURRENCY,
});

export const getEventKey = (eventId: string, timeslotId: string | undefined) =>
  [eventId, timeslotId].filter((t) => !!t && !!t.length).join('-');

export const getSubTicketIcon = (ticket: AccountTicketFragment) => {
  switch (ticket.ticketType) {
    case 'Parking':
      return 'Parking';
    case 'CreditWallet':
      return 'Wallet';
    case 'EventAccess':
      return 'Ticket';
    default:
      return undefined;
  }
};

/**
 * Appends locale as a query parameter to external ticket URLs.
 *
 * @param url - The external URL to append locale to
 * @param context - Context information for error reporting (service name and function)
 * @returns The URL with locale appended, or original URL if error occurs
 */
export const appendLocaleToExternalTicketUrl = (
  url: string | null | undefined,
  locale: string,
  context: { service: string; func: string },
): string | null | undefined => {
  if (!url) {
    return url;
  }

  try {
    const urlObj = new URL(url);
    if (locale) urlObj.searchParams.set('language', locale);

    return urlObj.toString();
  } catch (error) {
    captureException(error as Error, {
      extra: {
        tags: {
          url,
          service: context.service,
          func: context.func,
        },
      },
    });
    return url;
  }
};
